import {
  LIMITE_PUBLICACIONES_ACTIVAS_FREE,
  LIMITE_PUBLICACIONES_ACTIVAS_PREMIUM,
} from '../monetizacion/planes.js';
import {
  UMBRAL_NIVEL_2,
  UMBRAL_NIVEL_3,
  UMBRAL_NIVEL_4,
  UMBRAL_NIVEL_5,
  VENTANA_VENTAS_DIAS,
  TRIAL_DIAS,
  COMISION_PCT_POR_NIVEL,
} from '../monetizacion/vendedorNiveles.js';
import { escapeHtml as esc } from './escapeHtml.js';
import { mlvApplyZ } from './mlvZLayers.js';
import { mlvSaveFocus } from './mlvFocusRestore.js';
import { mlvModalHistoryAttach } from '../navigation/mlvModalHistoryBridge.js';
import { mlvTrack } from '../telemetry/mlvTrack.js';

const MODAL_ID = 'mlv-negocio-explainer-modal';

/** @typedef {'cupos' | 'nivel' | 'comision' | 'premium' | 'publicidad'} MlvExplainerSection */

const SECTIONS = /** @type {const} */ ([
  'cupos',
  'nivel',
  'comision',
  'premium',
  'publicidad',
]);

/**
 * Centro único: cupos, nivel, comisión, Premium y publicidad (progressive disclosure).
 * @param {{ section?: MlvExplainerSection }} [opts]
 * @returns {Promise<void>}
 */
export function openNegocioVecinoExplainer(opts = {}) {
  document.getElementById(MODAL_ID)?.remove();

  let sec = String(opts.section || 'cupos');
  if (!SECTIONS.includes(sec)) sec = 'cupos';

  const c1 = COMISION_PCT_POR_NIVEL[1];
  const c2 = COMISION_PCT_POR_NIVEL[2];
  const c3 = COMISION_PCT_POR_NIVEL[3];
  const c4 = COMISION_PCT_POR_NIVEL[4];
  const c5 = COMISION_PCT_POR_NIVEL[5];

  return new Promise((resolve) => {
    const restoreFocus = mlvSaveFocus();
    const wrap = document.createElement('div');
    wrap.id = MODAL_ID;
    wrap.className =
      'fixed inset-0 flex items-end justify-center bg-black/50 p-4 backdrop-blur-[2px] sm:items-center';
    mlvApplyZ(wrap, 'negocioExplainer');
    wrap.setAttribute('role', 'dialog');
    wrap.setAttribute('aria-modal', 'true');
    wrap.setAttribute('aria-labelledby', 'mlv-explainer-title');

    const openAttr = (id) => (id === sec ? ' open' : '');

    wrap.innerHTML = `
      <div class="mlv-explainer-panel flex max-h-[88vh] w-full max-w-md flex-col rounded-t-3xl bg-white shadow-2xl ring-1 ring-slate-200/90 sm:max-h-[85vh] sm:rounded-3xl">
        <div class="flex shrink-0 items-center justify-between gap-2 border-b border-slate-100 px-5 py-4">
          <h2 id="mlv-explainer-title" class="font-heading text-lg font-bold text-slate-900" tabindex="-1">Cómo funciona el barrio</h2>
          <button type="button" class="mlv-explainer-close rounded-full p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-800" aria-label="Cerrar">
            <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        <div class="min-h-0 flex-1 overflow-y-auto px-5 pb-6 pt-2">
          <p class="mb-4 text-xs leading-relaxed text-slate-500">Todo en un solo lugar. Abrí cada tema solo si lo necesitás.</p>

          <details id="mlv-explainer-cupos" class="group mb-2 rounded-2xl border border-slate-200 bg-slate-50/50 px-3 py-1"${openAttr('cupos')}>
            <summary class="cursor-pointer list-none py-3 font-semibold text-slate-900 marker:content-none [&::-webkit-details-marker]:hidden flex items-center justify-between gap-2">
              Cupos de publicaciones
              <span class="text-slate-400 text-xs font-normal">Plan gratis y Premium</span>
            </summary>
            <div class="border-t border-slate-200/80 pb-3 pt-2 text-sm leading-relaxed text-slate-600">
              <p>En <strong class="text-slate-800">plan gratis</strong> podés tener hasta <strong class="text-slate-900">${esc(String(LIMITE_PUBLICACIONES_ACTIVAS_FREE))}</strong> publicaciones <em>activas</em> a la vez. En <strong class="text-slate-800">Premium</strong>, hasta <strong class="text-slate-900">${esc(String(LIMITE_PUBLICACIONES_ACTIVAS_PREMIUM))}</strong>.</p>
              <p class="mt-2">Cuando vendés algo, en <strong class="text-slate-800">Mis publicaciones</strong> tocá <strong class="text-slate-900">¡Lo vendí!</strong>: liberás el cupo y dejamos constancia para tu nivel y la comisión.</p>
            </div>
          </details>

          <details id="mlv-explainer-nivel" class="group mb-2 rounded-2xl border border-slate-200 bg-slate-50/50 px-3 py-1"${openAttr('nivel')}>
            <summary class="cursor-pointer list-none py-3 font-semibold text-slate-900 marker:content-none [&::-webkit-details-marker]:hidden flex items-center justify-between gap-2">
              Nivel de vendedor
              <span class="text-slate-400 text-xs font-normal">Ventas en ${esc(String(VENTANA_VENTAS_DIAS))} días</span>
            </summary>
            <div class="border-t border-slate-200/80 pb-3 pt-2 text-sm leading-relaxed text-slate-600">
              <p>Contamos las ventas que registrás en la app dentro de una ventana de <strong class="text-slate-900">${esc(String(VENTANA_VENTAS_DIAS))} días</strong>. A más ventas en esa ventana, <strong class="text-slate-900">sube tu nivel</strong>.</p>
              <p class="mt-2 text-xs text-slate-500">Umbrales orientativos: nivel 2 desde ${esc(String(UMBRAL_NIVEL_2))} ventas, nivel 3 ${esc(String(UMBRAL_NIVEL_3))}, nivel 4 ${esc(String(UMBRAL_NIVEL_4))}, nivel 5 ${esc(String(UMBRAL_NIVEL_5))} (valores actuales del marketplace).</p>
            </div>
          </details>

          <details id="mlv-explainer-comision" class="group mb-2 rounded-2xl border border-slate-200 bg-slate-50/50 px-3 py-1"${openAttr('comision')}>
            <summary class="cursor-pointer list-none py-3 font-semibold text-slate-900 marker:content-none [&::-webkit-details-marker]:hidden flex items-center justify-between gap-2">
              Comisión del marketplace
              <span class="text-slate-400 text-xs font-normal">Según nivel</span>
            </summary>
            <div class="border-t border-slate-200/80 pb-3 pt-2 text-sm leading-relaxed text-slate-600">
              <p>La comisión es un porcentaje sobre la venta; <strong class="text-slate-900">baja cuando sube tu nivel</strong>.</p>
              <ul class="mt-2 space-y-1 text-xs text-slate-700">
                <li>Nivel 1 → <strong>${esc(String(c1))}%</strong> · Nivel 2 → <strong>${esc(String(c2))}%</strong> · Nivel 3 → <strong>${esc(String(c3))}%</strong></li>
                <li>Nivel 4 → <strong>${esc(String(c4))}%</strong> · Nivel 5 → <strong>${esc(String(c5))}%</strong></li>
              </ul>
            </div>
          </details>

          <details id="mlv-explainer-premium" class="group mb-2 rounded-2xl border border-slate-200 bg-slate-50/50 px-3 py-1"${openAttr('premium')}>
            <summary class="cursor-pointer list-none py-3 font-semibold text-slate-900 marker:content-none [&::-webkit-details-marker]:hidden flex items-center justify-between gap-2">
              Premium y trial
              <span class="text-slate-400 text-xs font-normal">Más cupos y beneficios</span>
            </summary>
            <div class="border-t border-slate-200/80 pb-3 pt-2 text-sm leading-relaxed text-slate-600">
              <p><strong class="text-slate-900">Premium</strong> amplía los cupos de publicaciones activas y suma beneficios comerciales según el plan.</p>
              <p class="mt-2">Las cuentas nuevas pueden acceder a un <strong class="text-slate-900">trial de ${esc(String(TRIAL_DIAS))} días</strong> con beneficios tipo nivel 5 en comisión, según las reglas vigentes en la app.</p>
              <p class="mt-2 text-xs text-slate-500">Para contratar o dudas, usá <strong class="text-slate-700">Plan Profesional</strong> o <strong class="text-slate-700">Ver beneficios</strong> en tu perfil.</p>
            </div>
          </details>

          <details id="mlv-explainer-publicidad" class="group mb-2 rounded-2xl border border-slate-200 bg-slate-50/50 px-3 py-1"${openAttr('publicidad')}>
            <summary class="cursor-pointer list-none py-3 font-semibold text-slate-900 marker:content-none [&::-webkit-details-marker]:hidden flex items-center justify-between gap-2">
              Visibilidad y publicidad
              <span class="text-slate-400 text-xs font-normal">Destacados en el barrio</span>
            </summary>
            <div class="border-t border-slate-200/80 pb-3 pt-2 text-sm leading-relaxed text-slate-600">
              <p>Además del listado normal, podés acordar con el equipo <strong class="text-slate-900">destacados, banners o presencia para tu negocio</strong> en el barrio (según disponibilidad).</p>
              <p class="mt-2 text-xs text-slate-500">Desde <strong class="text-slate-700">perfil</strong> o <strong class="text-slate-700">tras publicar</strong> podés pedir por WhatsApp opciones y precios. No hay obligación de contratar.</p>
            </div>
          </details>
        </div>
      </div>
    `;

    const hist = mlvModalHistoryAttach(() => {
      document.removeEventListener('keydown', onKey);
      try {
        restoreFocus();
      } catch (_e) {
        /* ignore */
      }
      wrap.remove();
      mlvTrack('negocio_explainer_close', { section: sec });
      resolve();
    });
    function close() {
      hist.closeFromUi();
    }

    function onKey(e) {
      if (e.key === 'Escape') close();
    }

    document.body.appendChild(wrap);
    document.addEventListener('keydown', onKey);
    wrap.addEventListener('click', (e) => {
      if (e.target === wrap) close();
    });
    wrap.querySelector('.mlv-explainer-close')?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      close();
    });

    const panel = wrap.querySelector('.mlv-explainer-panel');
    if (panel) {
      panel.addEventListener('click', (e) => e.stopPropagation());
    }

    mlvTrack('negocio_explainer_open', { section: sec });

    requestAnimationFrame(() => {
      wrap.querySelector('#mlv-explainer-title')?.focus?.({ preventScroll: true });
      const target = wrap.querySelector(`#mlv-explainer-${sec}`);
      target?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
    });
  });
}
